import { env } from "@/shared/constants/env";
import { AlomainLogo } from "@/shared/ui";

type Props = {
  title: string;
  message: string;
  /** Trace technique — affichée en développement uniquement. */
  detail?: string;
};

/**
 * Présentation de l'écran d'erreur, partagée par les deux barrières :
 * `AppErrorBoundary` (erreurs de route) et `RootErrorBoundary` (erreurs hors
 * routing, dans les providers).
 *
 * ## Aucune dépendance à React Router — c'est délibéré
 * La barrière racine entoure le `RouterProvider` lui-même : si celui-ci n'a pas
 * pu se monter, `<Link>` et `useNavigate()` n'existent pas et jetteraient à
 * leur tour, remplaçant l'écran d'erreur par… une erreur. Les deux actions
 * passent donc par `window.location`, qui fonctionne toujours.
 *
 * ## Pourquoi recharger plutôt que naviguer
 * Après un plantage de rendu, l'arbre React est dans un état incertain. Une
 * navigation interne le réutiliserait ; un rechargement complet est le seul
 * moyen fiable de repartir propre.
 */
export function ErrorScreen({ title, message, detail }: Props) {
  return (
    <section className="flex min-h-dvh flex-col items-center justify-center bg-white px-6 py-10">
      <div className="w-full max-w-md text-center">
        <div className="mb-6 flex justify-center">
          <AlomainLogo height={40} />
        </div>

        <span aria-hidden className="mb-4 block text-5xl">
          🛠️
        </span>

        <h1 className="font-extrabold text-2xl text-navy-800">{title}</h1>
        <p className="mt-2 text-gray-500">{message}</p>

        <div className="mt-7 flex flex-col gap-2.5">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="flex h-[52px] w-full items-center justify-center rounded-btn bg-or-500 font-bold text-navy-800 transition-opacity hover:opacity-90"
          >
            Réessayer
          </button>

          <button
            type="button"
            onClick={() => {
              window.location.href = "/";
            }}
            className="flex h-[52px] w-full items-center justify-center rounded-btn bg-gray-100 font-bold text-navy-800 transition-colors hover:bg-gray-200"
          >
            Retour à l'accueil
          </button>
        </div>

        <p className="mt-6 text-xs text-gray-500">
          Si le problème persiste, contactez le support depuis votre profil.
        </p>

        {/* La trace est précieuse pour corriger, illisible pour un client, et
            elle expose la structure interne de l'app. Réservée au poste du
            développeur, dans un bloc replié qui ne s'impose à personne. */}
        {env.isDev && detail && (
          <details className="mt-8 text-left">
            <summary className="cursor-pointer text-xs font-bold text-gray-500">
              Détails techniques (développement)
            </summary>
            <pre className="mt-2 max-h-72 overflow-auto rounded-card bg-gray-50 p-3 text-[11px] leading-relaxed text-navy-800">
              {detail}
            </pre>
          </details>
        )}
      </div>
    </section>
  );
}
